import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { defaultPresaveServices } from 'fym-shared/src/smartlinks/presave';
import { TypedMemo } from '../../common/types';
import { ErrorAlert } from '../../common/views/ErrorAlert/ErrorAlert';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { CreationSheetHeader } from './CreationSheetHeader';
import { CreationSheetFooter } from './CreationSheetFooter';
import { CreationStartStage } from './CreationStartStage';
import { CreationResolvingStage } from './CreationResolvingStage';
import { CreationResolvedStage } from './CreationResolvedStage';
import { CreationManualStage } from './CreationManualStage';
import { CreationDuplicateStage } from './CreationDuplicateStage';
import { CreationBlankStage } from './CreationBlankStage';
import { CreationStage } from './types';
import { CreationPageType } from './inferPageType';
import { SlugState } from './useCreationSlug';
import { useCreationSheetState } from './useCreationSheetState';
import { useCreationSubmit } from './useCreationSubmit';
import { useSmartLinkSheetSong } from './useSmartLinkSheetSong';
import { useInferenceReason } from './useInferenceReason';
import { SMLCreationSheetProps } from './props';
import { WizardCoverOffer } from '../../coverEditor/entry/WizardCoverOffer';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the sheet; each part uses its own slice of it */
import styles from './SMLCreationSheet.module.scss';
/* eslint-enable css-modules/no-unused-class */

type BodyProps = Omit<SMLCreationSheetProps, 'isOpen'>;

/**
 * One sheet where there used to be a quiz and a wizard.
 *
 * It is mounted only while it is open, so closing it forgets everything and
 * the next release starts on a clean field.
 */
const SMLCreationSheetBodyComponent: React.FC<BodyProps> = ({
  accountId,
  closeModal,
  initialCatalogTrackMaid,
  initialCatalogItemUuid,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const state = useCreationSheetState({ accountId, initialCatalogTrackMaid, initialCatalogItemUuid });
  const { stage, draft, resolution, catalogRow, duplicateItem } = state;
  // Opened from the add-song Done step on this song (`&from=addSong`): publish at once (owner decision 6).
  const sheetSong = useSmartLinkSheetSong();
  const publishNow =
    sheetSong.isFromAddSong &&
    initialCatalogItemUuid !== undefined &&
    sheetSong.catalogItemUuid === initialCatalogItemUuid &&
    stage !== CreationStage.blank;

  const templateHeadings = useMemo(
    () => ({
      tour: intl.formatMessage({ id: 'app.sml.create.template.tour.heading', defaultMessage: 'Tour dates' }),
      merch: intl.formatMessage({ id: 'app.sml.create.template.merch.heading', defaultMessage: 'The drop' }),
    }),
    [intl]
  );

  const { submit, isCreating, error } = useCreationSubmit({
    accountId,
    stage,
    source: state.source,
    draft,
    resolution,
    catalogRow,
    manualFields: state.manualFields,
    blankName: state.blankName,
    template: state.template,
    templateHeadings,
    closeModal,
    publishNow,
  });

  const why = useInferenceReason(state.inference, draft.pageType);
  const isSlugUsable = draft.slug.state !== SlugState.taken && draft.slug.state !== SlugState.illegal;
  const isPresave = draft.pageType === CreationPageType.presave;

  const existingSmartLinkId = duplicateItem?.smartLinkId ?? undefined;

  const openExisting = useCallback(() => {
    if (existingSmartLinkId === undefined) {
      return;
    }
    closeModal();
    router.push(SMLRoutes.manage, { accountId, smartLinkId: existingSmartLinkId });
  }, [accountId, closeModal, existingSmartLinkId, router]);

  const canCreate = useMemo(() => {
    if (isCreating || !isSlugUsable) {
      return false;
    }
    switch (stage) {
      case CreationStage.resolved:
        return isPresave ? draft.releaseDate !== '' : resolution.main !== undefined;
      case CreationStage.manual:
        return state.manualFields.title !== '' && draft.releaseDate !== '';
      case CreationStage.blank:
        return state.blankName !== '' && draft.slug.value !== '';
      // Nothing is ready to create on any of these: the first has no subject,
      // the second has not read it yet and the third is a question.
      case CreationStage.start:
      case CreationStage.resolving:
      case CreationStage.duplicate:
      default:
        return false;
    }
  }, [
    draft.releaseDate,
    draft.slug.value,
    isCreating,
    isPresave,
    isSlugUsable,
    resolution.main,
    stage,
    state.blankName,
    state.manualFields.title,
  ]);

  const footerNote = useMemo(() => {
    switch (stage) {
      case CreationStage.resolving:
        return intl.formatMessage({
          id: 'app.sml.create.foot.resolving',
          defaultMessage: 'Reading the release. You can keep going before this finishes.',
        });
      case CreationStage.resolved:
        if (publishNow) {
          return intl.formatMessage({
            id: 'app.sml.create.foot.publishNow',
            defaultMessage: 'Creates the page and puts it live at once, so you can share the link today.',
          });
        }
        if (isPresave) {
          return intl.formatMessage({
            id: 'app.sml.create.foot.presave',
            defaultMessage: 'Creates a draft with a pre-save block, a countdown and email capture.',
          });
        }
        // More services can still answer, so the note must not promise a count
        // that is about to change under the artist.
        if (resolution.isResolving) {
          return intl.formatMessage({
            id: 'app.sml.create.foot.release.searching',
            defaultMessage: 'Creates a draft with a listen block for every service we find.',
          });
        }
        return intl.formatMessage(
          {
            id: 'app.sml.create.foot.release',
            defaultMessage: 'Creates a draft with a listen block for the {count} services we found.',
          },
          { count: resolution.foundSources.length }
        );
      case CreationStage.manual:
        return intl.formatMessage(
          {
            id: 'app.sml.create.foot.manual',
            defaultMessage:
              'Fans can pre-save on {count} services before the release exists. It goes into your catalog too.',
          },
          { count: defaultPresaveServices.length }
        );
      case CreationStage.duplicate:
        return intl.formatMessage({
          id: 'app.sml.create.foot.duplicate',
          defaultMessage: 'Or make a second page anyway — for a campaign with its own numbers.',
        });
      case CreationStage.blank:
        return intl.formatMessage({
          id: 'app.sml.create.foot.blank',
          defaultMessage: 'No release attached, so no services and no pre-save block.',
        });
      case CreationStage.start:
      default:
        return intl.formatMessage({
          id: 'app.sml.create.foot.start',
          defaultMessage: 'Nothing is published until you say so. Every page starts as a draft.',
        });
    }
  }, [intl, isPresave, publishNow, resolution.foundSources.length, resolution.isResolving, stage]);

  const createLabel = intl.formatMessage({ id: 'app.sml.create.submit', defaultMessage: 'Create page' });

  return (
    <>
      <CreationSheetHeader onBack={stage === CreationStage.start ? undefined : state.goToStart} onClose={closeModal} />

      <div className={styles.body}>
        {error !== undefined && <ErrorAlert error={error} />}

        {stage === CreationStage.start && (
          <CreationStartStage
            query={state.query}
            onQueryChange={state.setQuery}
            onSubmitQuery={state.submitQuery}
            search={state.search}
            catalogItems={state.catalogItems}
            catalogTotal={state.catalogItems.length}
            now={state.now}
            onPickCatalogItem={state.pickCatalogItem}
            onPickTrack={state.pickTrack}
            onStartBlank={state.startBlank}
          />
        )}

        {stage === CreationStage.resolving && (
          <CreationResolvingStage
            resolution={resolution}
            fallbackTitle={catalogRow?.title ?? state.query}
            fallbackSubtitle={catalogRow?.artistName}
            coverUrl={state.coverUrl}
          />
        )}

        {stage === CreationStage.resolved && catalogRow !== undefined && (
          <WizardCoverOffer
            accountId={accountId}
            catalogItemUuid={catalogRow.uuid}
            hasCover={catalogRow.artworkFile !== null || catalogRow.artworkUrl !== null}
          />
        )}
        {stage === CreationStage.resolved && (
          <CreationResolvedStage
            resolution={resolution}
            fallbackTitle={catalogRow?.title ?? state.query}
            fallbackSubtitle={catalogRow?.artistName}
            coverUrl={state.coverUrl}
            why={why}
            draft={draft}
            slugPrefix={state.slugPrefix}
            today={state.today}
          />
        )}

        {stage === CreationStage.manual && (
          <CreationManualStage
            query={state.source.kind === 'manual' ? state.source.query : state.query}
            fields={state.manualFields}
            onFieldChange={state.setManualField}
            draft={draft}
            slugPrefix={state.slugPrefix}
            today={state.today}
            onStartBlank={state.startBlank}
          />
        )}

        {stage === CreationStage.duplicate && duplicateItem !== undefined && (
          <CreationDuplicateStage item={duplicateItem} slugPrefix={state.slugPrefix} onOpenExisting={openExisting} />
        )}

        {stage === CreationStage.blank && (
          <CreationBlankStage
            name={state.blankName}
            onNameChange={state.setBlankName}
            template={state.template}
            onTemplateChange={state.setTemplate}
            draft={draft}
            slugPrefix={state.slugPrefix}
          />
        )}
      </div>

      {stage === CreationStage.duplicate ? (
        <CreationSheetFooter
          note={footerNote}
          secondaryLabel={intl.formatMessage({
            id: 'app.sml.create.duplicate.anyway',
            defaultMessage: 'Create anyway',
          })}
          onSecondary={state.acceptDuplicate}
          primaryLabel={intl.formatMessage({
            id: 'app.sml.create.duplicate.open',
            defaultMessage: 'Open existing page',
          })}
          onPrimary={openExisting}
        />
      ) : (
        <CreationSheetFooter
          note={footerNote}
          onBack={stage === CreationStage.start ? undefined : state.goToStart}
          onCancel={stage === CreationStage.start ? closeModal : undefined}
          primaryLabel={stage === CreationStage.start ? undefined : createLabel}
          primaryDisabled={!canCreate}
          onPrimary={stage === CreationStage.start ? undefined : submit}
        />
      )}
    </>
  );
};

export const SMLCreationSheetBody = TypedMemo(SMLCreationSheetBodyComponent);
